import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FieldType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { debounceTime, distinctUntilChanged, filter, Subscription, takeUntil } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import type { Entry } from '../../../features/admin/modules/modules.model';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { FuseAlertComponent } from '../../../../@fuse/components/alert';
import { UtilityService } from '../../services/utility.service';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { cloneDeep } from 'lodash-es';
import { EditFormRightsDirective } from '../../../core/auth/directives/can-edit-form.directive';
import { FormService } from '../../../features/admin/modules/features/form-builder/form.service';
import { FormGroup, TouchedChangeEvent, ValidatorFn } from '@angular/forms';
import { FormlyFormOptions } from '@ngx-formly/core';
import { UserStore } from '../../../core/services/user.store';
import { FormHelper } from '../../../features/admin/modules/features/form-builder/form.util';
import {
  DETAILS_SERVICE_TOKEN,
  DetailsService,
} from '../../../features/admin/modules/modules-entity/templates/details/details/details.service';
import { debugLog } from '../../utils/debug-logger';

@Component({
  selector: 'formly-inline-entries',
  template: `
    <div class="my-4">
      @if (props.label && !props.hideLabel) {
        @if (props.labelType === undefined || props.labelType === 'titel') {
          <h3 class="font-bold mb-2 flex items-center gap-1">
            {{ props.label }}
            <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
            @if (isLoading()) {
              <mat-spinner diameter="16" class="ml-2"></mat-spinner>
            }
          </h3>
        } @else {
          <label class="text-sm font-medium text-default mb-2 flex items-center gap-1">
            {{ props.label }}
            <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
            @if (isLoading()) {
              <mat-spinner diameter="16" class="ml-2"></mat-spinner>
            }
          </label>
        }
      }
      @if (props.description) {
        <p class="mb-2">{{ props.description }}</p>
      }

      @if (error()) {
        <fuse-alert class="mb-4" [type]="'error'">{{ error() }}</fuse-alert>
      }

      @if (entries().length > 0) {
        <div class="subforms-container">
          @for (entry of entries(); track entry.id; let i = $index) {
            <div class="flex justify-between mb-2 gap-4">
              <form class="flex-1">
                <formly-form
                  [form]="forms()[entry.id]"
                  [canEditForm]="{ form: forms()[entry.id], id: linkedModule()?.id }"
                  [options]="formOptions()[entry.id]"
                  [fields]="subfields()[entry.id]"
                  [model]="entry.entry">
                </formly-form>
              </form>

              <div class="flex-0 flex gap-2">
                @if (canMutate() && !props.hideButtons && props.initialCount < entries().length && !hideAddButton()) {
                  <button type="button" mat-button class="flex-0 max-h-fit " (click)="removeEntry(entry)">
                    <mat-icon>close</mat-icon>
                  </button>
                }
              </div>
            </div>
          }
        </div>
      } @else {
        <p class="text-sm text-accent">Geen {{ props.label ? props.label.toLowerCase() : 'items' }} aangemaakt</p>
      }

      <div class="py-4">
        @if (canMutate() && canAddMore()) {
          <button
            type="button"
            class="small-button dd-action-button"
            mat-flat-button
            [disabled]="isLoading()"
            (click)="addEntry()">
            <mat-icon>add</mat-icon>
            {{ props.addText || 'Item toevoegen' }}
          </button>
        }
      </div>
    </div>
  `,
  imports: [
    FormlyModule,
    MatButtonModule,
    MatIconModule,
    InfoTooltipComponent,
    MatProgressSpinner,
    FuseAlertComponent,
    EditFormRightsDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .subforms-container {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }
  `,
})
export class FormlyInlineEntriesComponent extends FieldType implements OnInit, OnDestroy {
  private detailsService = inject(DETAILS_SERVICE_TOKEN) as DetailsService;
  private destroy$ = injectDestroy();
  private entriesService = inject(EntriesService);
  private modulesStore = inject(ModulesStore);
  private userStore = inject(UserStore);
  formService = inject(FormService);

  route = inject(ActivatedRoute);
  params = toSignal(this.route.paramMap);

  error = signal<string | null>(null);
  isLoading = signal(false);
  linkedModule = computed(() => {
    return this.modulesStore.enabledEntities().find(module => module.code === this.props.linkedModule);
  });
  prevValue = signal<Entry[]>([]);
  hideAddButton = signal(false);
  entries = signal<Entry[]>([]);
  linkedEntryId = signal<Entry['id']>(undefined);
  updateSubscription: { [key: string]: Subscription } = {};
  changeValuesSubscriptions: { [key: string]: Subscription } = {};
  statusSubscriptions: { [key: string]: Subscription } = {};

  forms = signal<{ [key: string]: FormGroup }>({});
  subfieldsDefault: FormlyFieldConfig[];
  subfields = signal<{ [key: string]: FormlyFieldConfig[] }>({});
  private readonly validatorFn = this.inlineEntriesValidator();

  validator = this.inlineEntriesValidator();

  // Pending requests map: keeps only the latest request per entry (key = entry.id or 'new' for new entries)
  private pendingRequests = new Map<string, Entry>();
  private syncPrevParentValues: Record<string, unknown> = {};
  private isProcessing = false;

  formOptions = computed(() => {
    const options: FormlyFormOptions = {};
    this.entries().forEach(entry => {
      options[entry.id] = {
        formState: {
          entry: cloneDeep(entry),
          entity: cloneDeep(this.linkedModule()),
        },
      };
    });
    return options;
  });

  constructor() {
    effect(() => {
      this.entries().forEach(entry => {
        if (this.subfields()[entry.id] === undefined) {
          this.subfields.set({
            ...this.subfields(),
            [entry.id]: this.removeFieldIds(cloneDeep(this.subfieldsDefault)),
          });
        }
        if (this.forms()[entry.id] === undefined) {
          const form = new FormGroup({});
          if (!this.canMutate()) form.disable({ emitEvent: false });
          this.forms.set({
            ...this.forms(),
            [entry.id]: form,
          });
        }

        if (this.changeValuesSubscriptions[entry.id]) {
          this.changeValuesSubscriptions[entry.id].unsubscribe();
        }
        if (this.statusSubscriptions[entry.id]) {
          this.statusSubscriptions[entry.id].unsubscribe();
        }

        this.changeValuesSubscriptions[entry.id] = this.forms()
          [entry.id].valueChanges.pipe(takeUntil(this.destroy$), debounceTime(400), distinctUntilChanged())
          .subscribe(() => {
            if (!this.canMutate()) return;
            const formValue = this.forms()[entry.id].getRawValue();

            const prevValue = this.prevValue()?.find(prevEntryValue => {
              return prevEntryValue.id === entry.id;
            });

            if (!this.areEntryValuesEqual(prevValue?.entry, entry.entry)) {
              if (this.updateSubscription[entry.id]) {
                this.updateSubscription[entry.id].unsubscribe();
              }
              const updatedEntry = {
                ...cloneDeep(entry),
                entry: {
                  ...formValue,
                  // Preserve fields that might not be in the form
                  ModuleId: entry.entry?.ModuleId ? entry.entry?.ModuleId : undefined,
                },
              };
              this.saveEntry(updatedEntry);
              this.prevValue.set(this.entries());
            }
          });

        // Propagate sub-form validity to the parent so Formly's error display recalculates via statusChanges.
        this.statusSubscriptions[entry.id] = this.forms()
          [entry.id].statusChanges.pipe(takeUntil(this.destroy$))
          .subscribe(status => {
            this.field.formControl.updateValueAndValidity();
          });
      });
    });

    super();
    toObservable(this.entries)
      .pipe(takeUntilDestroyed())
      .subscribe(entries => {
        this.field.formControl.setValue(cloneDeep(entries), { emitEvent: false });
        // Cache entries in formState to survive tab switches
        this.cacheEntries(entries);
        // Force revalidation with event emission so Formly's error display updates
        this.field.formControl.updateValueAndValidity();
      });
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    Object.values(this.changeValuesSubscriptions).forEach(sub => sub.unsubscribe());
    Object.values(this.statusSubscriptions).forEach(sub => sub.unsubscribe());
    Object.values(this.updateSubscription).forEach(sub => sub.unsubscribe());

    // Formly instantiates a throwaway "probe" instance via resolveFieldTypeRef to
    // read defaultOptions and destroys it immediately. In that probe `field` is
    // never bound, so bail out before dereferencing formControl.
    if (!this.field?.formControl) {
      return;
    }

    this.field.formControl.removeValidators(this.validatorFn);
    this.field.formControl.updateValueAndValidity({ emitEvent: false });

    if (this.entries().length > 0) {
      this.cacheEntries(this.entries());
    }
  }

  ngOnInit() {
    const allFields = cloneDeep(this.modulesStore.generatedForms()[this.linkedModule().id]);

    // Filter out excluded fields if specified, preserving fieldGroup structure
    this.subfieldsDefault = this.props.excludedFields
      ? FormHelper.filterExcludedFields(allFields, this.props.excludedFields)
      : allFields;

    if (this.props.initialCount === undefined) {
      this.props.initialCount = 0;
    }

    if (this.props.maxEntries === undefined) {
      this.props.maxEntries = 1000;
    }

    this.linkedEntryId.set(this.options.formState.entry?.id);

    if (this.linkedModule()) {
      this.loadExistingEntries();
    }

    if (this.props.hideAddButtonForRoles != null && Object.keys(this.props.hideAddButtonForRoles).length > 0) {
      this.hideAddButton.set(this.field.props.hideAddButtonForRoles[this.userStore.currentUserId()]);
    }

    // Register custom validator so parent form includes sub-form validity
    this.field.formControl.addValidators(this.validatorFn);
    this.field.formControl.updateValueAndValidity();

    // Propagate touched state to sub-forms (so red borders appear on save attempt)
    this.field.formControl.events
      .pipe(
        filter((e): e is TouchedChangeEvent => e instanceof TouchedChangeEvent),
        filter(e => e.touched),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        Object.values(this.forms()).forEach(f => f.markAllAsTouched());
      });

    this.field.formControl.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.canMutate()) {
        Object.values(this.forms()).forEach(form => form.disable({ emitEvent: false }));
      }
    });

    this.initParentSync();
  }

  /**
   * Load existing entries related to the parent entry
   */
  private loadExistingEntries(): void {
    if (!this.linkedModule()) {
      this.error.set('Gekoppelde module niet gevonden');
      return;
    }

    this.isLoading.set(true);
    this.error.set(null);

    this.validateAndLoadEntries();
    this.isLoading.set(false);
  }

  validateAndLoadEntries() {
    // Check formState cache first — it survives tab switches where Formly's registerControl
    // overwrites the FormControl with stale model data.
    const cached = this.getCachedEntries();
    if (Array.isArray(cached) && cached.length > 0 && cached[0]?.id) {
      const restored = cloneDeep(cached);
      this.entries.set(restored);
      this.prevValue.set(cloneDeep(restored));
      return;
    }

    // Fallback to FormControl value on first load (no tab switch yet).
    try {
      if (Array.isArray(this.field.formControl.value) && this.field.formControl.value[0].id) {
        this.entries.set(this.field.formControl.value);
        this.prevValue.set(this.entries());
      } else {
        this.field.formControl.patchValue([]);
        this.entries.set([]);
      }
    } catch {
      this.field.formControl.patchValue([]);
      this.entries.set([]);
    }
  }

  addEntry() {
    if (!this.canMutate()) return;
    if (Object.keys(this.forms).length === 0) {
      this.field.formControl.addValidators(this.inlineEntriesValidator());
    }

    let newEntry: Entry = {
      id: undefined,
      entry: {
        archived: false,
        ...this.formService.getDefaultValues(this.subfieldsDefault),
      },
    };

    newEntry = this.formService.fillEntryWithMappingItems(
      newEntry,
      this.field.props.defaultValuesLinkedItems,
      this.linkedEntryId(),
      cloneDeep({
        ...this.options.formState?.entry,
        id: this.detailsService.entryId(),
        selectedInput: UtilityService.keyPathToValue(
          this.options.formState?.entry,
          this.options.formState?.module?.titelKeyPath
        ),
      })
    );
    this.saveEntry(newEntry);
  }

  /**
   * Queue an entry save request. New requests replace old ones for the same entry.
   * Only the most recent version is kept and processed.
   */
  saveEntry(entry: Entry): void {
    if (!this.linkedModule() || !this.canMutate()) return;

    this.isLoading.set(true);

    // Use entry.id as key, or 'new_' + timestamp for new entries
    const key = entry?.id ?? `new_${Date.now()}`;
    this.pendingRequests.set(key, entry);

    if (!this.isProcessing) {
      this.processNextRequest();
    }
  }

  /**
   * Process the next pending request. After completion, checks for more pending requests.
   */
  private processNextRequest(): void {
    if (!this.canMutate()) {
      this.pendingRequests.clear();
      this.isProcessing = false;
      this.isLoading.set(false);
      return;
    }
    // Get first pending request
    const nextKey = this.pendingRequests.keys().next().value;
    if (!nextKey) {
      this.isProcessing = false;
      this.isLoading.set(false);
      return;
    }

    this.isProcessing = true;
    const entry = this.pendingRequests.get(nextKey);
    this.pendingRequests.delete(nextKey);

    if (!entry) {
      this.processNextRequest();
      return;
    }

    if (entry?.id) {
      this.executeUpdateRequest(entry);
    } else {
      this.executeCreateRequest(entry);
    }
  }

  /**
   * Execute an update request for an existing entry
   */
  private executeUpdateRequest(entry: Entry): void {
    if (!this.canMutate()) return;
    this.entriesService
      .editEntry(this.linkedModule().id, entry.id, entry)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: updatedEntry => {
          const entries = this.entries().map(existingEntry => {
            if (existingEntry.id === updatedEntry.id) {
              const compareEntry = cloneDeep(updatedEntry);

              // Live form values are leading, not the stale signal
              const liveFormValue = this.forms()[updatedEntry.id]?.getRawValue();
              const currentEntryValues = liveFormValue
                ? { ...existingEntry.entry, ...liveFormValue }
                : existingEntry.entry;

              const newEntry = {
                ...updatedEntry,
                entry: {
                  ...updatedEntry.entry,
                  ...currentEntryValues,
                },
              };

              if (JSON.stringify(compareEntry.entry) !== JSON.stringify(currentEntryValues)) {
                this.saveEntry(newEntry);
              }

              return newEntry;
            }

            // Merge live form values for other entries to prevent Formly from reverting
            // sub-form values to stale signal data while the user is typing.
            const otherLiveFormValue = this.forms()[existingEntry.id]?.getRawValue();
            if (otherLiveFormValue) {
              return { ...existingEntry, entry: { ...existingEntry.entry, ...otherLiveFormValue } };
            }
            return existingEntry;
          });
          this.entries.set(cloneDeep(entries));
          // Revalidate parent without emitEvent:false so Formly's FormlyValidationMessage
          // receives the statusChanges event and clears the error.
          this.processNextRequest();
        },
        error: err => {
          console.error('Error updating entry:', err);
          this.error.set(UtilityService.createErrorMessage(err));
          this.processNextRequest();
        },
      });
  }

  /**
   * Execute a create request for a new entry
   */
  private executeCreateRequest(entry: Entry): void {
    if (!this.canMutate()) return;
    this.entriesService
      .addEntry(this.linkedModule().id, entry.entry)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: newEntry => {
          const updatedEntries = [...this.entries(), newEntry];
          this.entries.set(updatedEntries);
          this.prevValue.set(cloneDeep(updatedEntries)); // Prevent immediate auto-save trigger
          this.processNextRequest();
        },
        error: err => {
          console.error('Error creating entry:', err);
          this.error.set(UtilityService.createErrorMessage(err));
          this.processNextRequest();
        },
      });
  }

  /**
   * Remove an entry from both the form and the server.
   * Applies optimistic update: immediately removes from UI, reverts on error.
   */
  removeEntry(entry: Entry): void {
    if (!this.linkedModule() || !this.canMutate()) return;

    this.isLoading.set(true);

    // Optimistic update: remove immediately from UI
    const previousEntries = this.entries();
    this.entries.set(previousEntries.filter(e => e.id !== entry.id));

    // Clean up the removed entry's form, subfields and subscriptions
    const { [entry.id]: _removedForm, ...remainingForms } = this.forms();
    this.forms.set(remainingForms);

    const { [entry.id]: _removedFields, ...remainingSubfields } = this.subfields();
    this.subfields.set(remainingSubfields);

    if (this.changeValuesSubscriptions[entry.id]) {
      this.changeValuesSubscriptions[entry.id].unsubscribe();
      delete this.changeValuesSubscriptions[entry.id];
    }
    if (this.statusSubscriptions[entry.id]) {
      this.statusSubscriptions[entry.id].unsubscribe();
      delete this.statusSubscriptions[entry.id];
    }
    if (this.updateSubscription[entry.id]) {
      this.updateSubscription[entry.id].unsubscribe();
      delete this.updateSubscription[entry.id];
    }

    if (Object.keys(this.forms()).length === 0) {
      this.field.formControl.removeValidators(this.inlineEntriesValidator());
    }

    this.field.formControl.updateValueAndValidity();

    this.entriesService
      .delete(this.linkedModule().id, entry.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoading.set(false);
        },
        error: err => {
          console.error('Error deleting entry:', err);
          // Revert optimistic update
          this.entries.set(previousEntries);
          this.field.formControl.updateValueAndValidity();
          this.error.set(UtilityService.createErrorMessage(err));
          this.isLoading.set(false);
        },
      });
  }

  /**
   * Build a unique cache key by walking the parent chain.
   * Inside a repeater the key parts include the repeater index,
   * producing e.g. "repeaterKey.0.inlineEntriesKey" vs "repeaterKey.1.inlineEntriesKey".
   */
  private getCacheKey(): string {
    const parts: string[] = [];
    let current: FormlyFieldConfig | undefined = this.field;
    while (current) {
      if (current.key != null) {
        parts.unshift(String(current.key));
      }
      current = current.parent;
    }
    return parts.join('.') || this.field.id;
  }

  private cacheEntries(entries: Entry[]): void {
    if (!this.options?.formState) return;
    if (!this.options.formState.tempInlineEntries) {
      this.options.formState.tempInlineEntries = {};
    }
    this.options.formState.tempInlineEntries[this.getCacheKey()] = cloneDeep(entries);
  }

  private getCachedEntries(): Entry[] | undefined {
    return this.options?.formState?.tempInlineEntries?.[this.getCacheKey()];
  }

  canAddMore(): boolean {
    return (this.model?.length || 0) < this.props.maxEntries;
  }

  canMutate(): boolean {
    return (
      !this.field?.formControl?.disabled &&
      !this.props.disabled &&
      !this.props.readonly &&
      !this.props['readOnly'] &&
      !this.form?.disabled
    );
  }

  private inlineEntriesValidator(): ValidatorFn {
    return () => {
      const forms = this.forms();

      const entryIds = new Set(this.entries().map(e => e.id));
      const invalidFieldLabels: string[] = [];

      Object.entries(forms).forEach(([id, form]) => {
        if (form.invalid) {
          const fields = FormHelper.getFields(this.subfields()[id] ?? []);
          // Use FormGroup controls as source of truth, not the potentially stale formControl references on Formly fields.
          Object.entries(form.controls).forEach(([key, control]) => {
            if (control.invalid) {
              const field = fields.find(f => String(f.key) === key);
              if (field?.props?.label) {
                invalidFieldLabels.push(field.props.label);
              }
            }
          });
        }
      });

      if (invalidFieldLabels.length > 0) {
        return { inlineEntriesInvalid: [...new Set(invalidFieldLabels)] };
      }
      return null;
    };
  }

  /**
   * Subscribes to parent form changes. When a field referenced in
   * `defaultValuesLinkedItems` (basedOn: 'dynamic') changes, syncs the new
   * value to all existing inline entries.
   */
  private initParentSync(): void {
    const mappings = (this.field.props.defaultValuesLinkedItems ?? []).filter(
      (m): m is { targetField: string; basedOn: string; sourceField: string } =>
        m.basedOn === 'dynamic' && !!m.sourceField
    );

    if (!mappings.length) return;

    const toFormPath = (keyPath: string) => keyPath.replace(/^entry\./, '');

    // Snapshot of current parent values to detect changes
    const initialValue = this.form.root.value;
    for (const m of mappings) {
      this.syncPrevParentValues[m.sourceField] = cloneDeep(
        UtilityService.keyPathToValue(initialValue, toFormPath(m.sourceField))
      );
    }

    this.form.root.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(300)).subscribe(rootValue => {
      if (!this.canMutate()) return;
      const changed = mappings.filter(m => {
        const newVal = UtilityService.keyPathToValue(rootValue, toFormPath(m.sourceField));
        return JSON.stringify(this.syncPrevParentValues[m.sourceField]) !== JSON.stringify(newVal);
      });

      if (!changed.length) return;

      this.syncMappingsToEntries(changed, rootValue);
    });
  }

  /**
   * Applies changed parent values to all existing inline entries and saves them.
   */
  private syncMappingsToEntries(
    changedMappings: { targetField: string; sourceField: string }[],
    rootValue: Record<string, unknown>
  ): void {
    if (!this.canMutate()) return;
    const toFormPath = (keyPath: string) => keyPath.replace(/^entry\./, '');

    // Merge entries() and cache, deduplicating by ID. entries() is the primary source;
    // the cache may contain entries not yet in the signal (e.g. right after creation before the API response).
    const seenIds = new Set<string>();
    const currentEntries = [...this.entries(), ...(this.getCachedEntries() ?? [])].filter(e => {
      if (!e.id || seenIds.has(e.id)) return false;
      seenIds.add(e.id);
      return true;
    });

    if (!currentEntries.length) {
      return; // Do not update the snapshot — sync will be retried on the next emit.
    }

    // Update snapshot now that we have entries to sync.
    for (const m of changedMappings) {
      this.syncPrevParentValues[m.sourceField] = cloneDeep(
        UtilityService.keyPathToValue(rootValue, toFormPath(m.sourceField))
      );
    }

    const syncedEntries: Entry[] = [];

    for (const entry of currentEntries) {
      const currentValues: Record<string, unknown> = {};
      const newValues: Record<string, unknown> = {};

      // Merge live sub-form values into the entry before syncing. entries() may contain
      // stale data if an API response is still in flight. Merging live values first
      // prevents unsaved edits from being overwritten by the sync.
      const subForm = this.forms()[entry.id];
      const updatedEntry = cloneDeep(entry);
      if (subForm) {
        const liveFormValue = subForm.getRawValue();
        Object.assign(updatedEntry.entry, liveFormValue);
      }

      for (const mapping of changedMappings) {
        const newValue = UtilityService.keyPathToValue(rootValue, toFormPath(mapping.sourceField));
        currentValues[mapping.targetField] = UtilityService.keyPathToValue(entry, mapping.targetField);
        newValues[mapping.targetField] = newValue;

        UtilityService.setKeyValuePath(updatedEntry, mapping.targetField, newValue);

        // Patch sub-form directly without triggering a save.
        if (subForm) {
          subForm.get(toFormPath(mapping.targetField))?.patchValue(newValue, { emitEvent: false });
        }
      }

      syncedEntries.push(updatedEntry);
    }

    this.entries.update(entries =>
      entries.map(e => {
        const synced = syncedEntries.find(s => s.id === e.id);
        return synced ?? e;
      })
    );

    // Also update the cache directly as it may contain entries not yet in entries().
    this.cacheEntries(syncedEntries);

    // patchValue() uses emitEvent:false so statusChanges does not emit on sub-forms.
    // Force parent revalidation so Formly's error display updates.
    for (const updatedEntry of syncedEntries) {
      this.saveEntry(updatedEntry);
    }
  }

  private areEntryValuesEqual(formValue: any, entryValue: any): boolean {
    if (formValue == null) {
      return false;
    }
    // Ignore system fields for comparison
    const formKeys = Object.keys(formValue).filter(key => key !== 'ModuleId' && key !== 'archived' && key !== '_etag');

    for (const key of formKeys) {
      // Skip undefined/null comparisons or empty arrays vs undefined
      if (
        (formValue[key] === undefined || formValue[key] === null) &&
        (entryValue[key] === undefined || entryValue[key] === null)
      ) {
        continue;
      }

      // For arrays, compare the stringified versions
      if (Array.isArray(formValue[key]) && Array.isArray(entryValue[key])) {
        if (JSON.stringify(formValue[key]) !== JSON.stringify(entryValue[key])) {
          return false;
        }
        continue;
      }

      // For objects, use deep comparison
      if (
        typeof formValue[key] === 'object' &&
        formValue[key] !== null &&
        typeof entryValue[key] === 'object' &&
        entryValue[key] !== null
      ) {
        if (!this.areEntryValuesEqual(formValue[key], entryValue[key])) {
          return false;
        }
        continue;
      }

      if (formValue[key] !== entryValue[key]) {
        return false;
      }
    }

    return true;
  }

  private removeFieldIds(fields: FormlyFieldConfig[]): FormlyFieldConfig[] {
    const clonedFields = cloneDeep(fields);

    const processField = (field: FormlyFieldConfig) => {
      if (field.id) {
        field.id = undefined;
      }

      if (field.fieldGroup && field.fieldGroup.length > 0) {
        field.fieldGroup.forEach((childField: FormlyFieldConfig) => processField(childField));
      }

      if (field.fieldArray?.['fieldGroup'] && field.fieldArray['fieldGroup']?.length > 0) {
        field.fieldArray['fieldGroup'].forEach((childField: FormlyFieldConfig) => processField(childField));
      }
    };

    clonedFields.forEach(field => processField(field));
    return clonedFields;
  }
}
